import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiCalendar, FiCheck } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { convertToINR } from '../utils/currency';

const RentProduct = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [startDate, setStartDate] = useState(new Date());
  const [duration, setDuration] = useState(1); // Duration in days
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');

  const getSecurityCharge = () => {
    if (!product) return 0;
    return product.securityCharge || 0;
  };

  // Fetch product details
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/products/${productId}`);
        setProduct(response.data.product);
      } catch (err) {
        setError('Failed to load product details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  // Calculate rental end date based on duration
  const calculateEndDate = () => {
    const end = new Date(startDate);
    end.setDate(end.getDate() + duration);
    return end;
  };

  const endDate = calculateEndDate();
  const securityCharge = getSecurityCharge();
  const rentalAmount = product ? duration * convertToINR(product.pricePerDay) : 0;
  const totalPrice = rentalAmount + securityCharge;

  // Handle rent submission
  const handleSubmitRent = async (e) => {
    e.preventDefault();

    // Validation
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/rent/${productId}` } });
      return;
    }

    if (user?.role !== 'buyer') {
      setError('Only buyers can rent products');
      return;
    }

    if (endDate <= startDate) {
      setError('Duration must be at least 1 day');
      return;
    }

    if (!deliveryAddress.trim()) {
      setError('Please enter a delivery address');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await api.post('/bookings', {
        productId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        deliveryAddress,
        notes
      });

      setSuccess('Booking confirmed successfully!');
      setTimeout(() => {
        navigate('/buyer/bookings');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create booking');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#F27D16] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">Product not found</p>
          <button
            onClick={() => navigate('/products')}
            className="px-6 py-2 bg-[#F27D16] text-white rounded-lg hover:bg-[#D96D0E]"
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const defaultImage = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=600&fit=crop';

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#222222]">Rent Product</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Product Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Image and Info */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Image */}
              <div className="h-96 bg-[#FFF9D4] overflow-hidden">
                <img
                  src={product.images?.[0] || defaultImage}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Product Details */}
              <div className="p-6 space-y-4">
                <div>
                  <h2 className="text-3xl font-bold text-[#222222] mb-2">{product.title}</h2>
                  <p className="text-gray-600 text-lg mb-4">{product.description}</p>

                  {/* Category and Availability */}
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="px-3 py-1 bg-[#FFF9D4] text-[#F27D16] rounded-full text-sm font-medium">
                      {product.category}
                    </span>
                    {product.isAvailable && (
                      <span className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                        <FiCheck className="w-4 h-4" />
                        <span>Available</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Seller Info */}
                {product.seller && (
                  <div className="p-4 bg-[#FFF9D4] rounded-lg">
                    <p className="text-xs text-gray-600 mb-2">SELLER</p>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-[#F27D16] flex items-center justify-center text-white font-bold">
                        {product.seller.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-[#222222]">
                          {product.seller.businessName || product.seller.name}
                        </p>
                        <p className="text-sm text-gray-600">{product.seller.email}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Price Info */}
                <div className="p-4 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Daily Rate</p>
                  <p className="text-2xl font-bold text-[#F27D16]">
                    ₹{convertToINR(product.pricePerDay).toLocaleString('en-IN')}
                    <span className="text-sm font-normal text-gray-600">/day</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Booking Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24 space-y-6">
              <h3 className="text-xl font-bold text-[#222222]">Rental Details</h3>

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg text-sm">
                  {success}
                </div>
              )}

              <form onSubmit={handleSubmitRent} className="space-y-4">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-[#386646] mb-2">
                    <FiCalendar className="inline-block w-4 h-4 mr-2" />
                    Pickup Date
                  </label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    minDate={new Date()}
                    dateFormat="MMM dd, yyyy"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F27D16]"
                  />
                </div>

                {/* Duration Selector */}
                <div>
                  <label className="block text-sm font-medium text-[#386646] mb-2">
                    <FiCalendar className="inline-block w-4 h-4 mr-2" />
                    Rental Duration
                  </label>
                  <div className="space-y-2">
                    <select
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F27D16]"
                    >
                      {[1, 2, 3, 5, 7, 10, 14, 21, 30].map((days) => (
                        <option key={days} value={days}>
                          {days} {days === 1 ? 'day' : 'days'}
                        </option>
                      ))}
                    </select>
                    <div className="text-sm text-gray-600">
                      Expected Return: <span className="font-medium">{endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>

                {/* Duration Display */}
                <div className="p-3 bg-[#FFF9D4] rounded-lg">
                  <p className="text-sm text-gray-600">Total Rental Period</p>
                  <p className="text-lg font-bold text-[#F27D16]">
                    {duration} {duration === 1 ? 'day' : 'days'}
                  </p>
                </div>

                {/* Delivery Address */}
                <div>
                  <label className="block text-sm font-medium text-[#386646] mb-2">
                    <FiMapPin className="inline-block w-4 h-4 mr-2" />
                    Delivery Address
                  </label>
                  <textarea
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Enter your delivery address"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F27D16] resize-none"
                    rows="3"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-[#386646] mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any special requests or notes for the seller"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F27D16] resize-none"
                    rows="2"
                  />
                </div>

                {/* Price Breakdown */}
                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Rental Charge (₹{convertToINR(product.pricePerDay).toLocaleString('en-IN')} × {duration} {duration === 1 ? 'day' : 'days'})
                    </span>
                    <span className="font-medium text-[#222222]">₹{rentalAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Security Deposit ({product.category})</span>
                    <span className="font-medium text-[#222222]">₹{securityCharge.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded border border-blue-200">
                    ℹ️ Security deposit is fully refundable if the product is returned without any damage.
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="font-semibold text-[#222222]">Total Amount</span>
                    <span className="text-2xl font-bold text-[#F27D16]">₹{totalPrice.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting || !isAuthenticated || (user?.role !== 'buyer')}
                  className="w-full py-3 bg-[#F27D16] text-white font-semibold rounded-lg hover:bg-[#D96D0E] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Processing...' : 'Confirm Rental'}
                </button>

                {!isAuthenticated && (
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="w-full py-3 border-2 border-[#F27D16] text-[#F27D16] font-semibold rounded-lg hover:bg-[#FFF9D4] transition-colors"
                  >
                    Login to Rent
                  </button>
                )}

                {isAuthenticated && user?.role !== 'buyer' && (
                  <p className="text-sm text-gray-600 text-center p-3 bg-yellow-100 rounded-lg">
                    Only buyers can rent products. Please switch to buyer account.
                  </p>
                )}
              </form>

              {/* Terms */}
              <div className="text-xs text-gray-600 pt-4 border-t border-gray-200 space-y-2">
                <p>
                  <strong>Terms & Conditions:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>By confirming the rental, you agree to our rental terms and conditions.</li>
                  <li>Security deposit is <strong>fully refundable</strong> if the product is returned without any damage.</li>
                  <li>The seller will review your booking request and confirm availability.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentProduct;
